/**
 * Per-tool canvas metadata — display name + routes.
 *
 * Two distinct destinations per tool:
 *   - `href`          where canvas CONTENT lives. Used by CanvasActionCard
 *                     ("Show me" → take me to where the AI just wrote).
 *   - `workspaceHref` the persona's landing zone — the rich exploratory
 *                     surface (codebook picker, KOL atlas, layer tabs).
 *                     Used by RouteHandoffCard ("Continue with X" — start
 *                     a session here) and as the back-target from canvas
 *                     pages.
 *
 * Single source of truth shared between CanvasActionCard, RouteHandoffCard,
 * the use-chat.ts rewrite logic, and the server-side conversation-history
 * canvas-context label. When a new tool gains a canvas, add an entry here
 * and every consumer picks it up.
 */

import { canvasMetaFor } from '@/lib/workspaces/registry'

export type ToolId =
  | 'concierge'
  | 'study-design'
  | 'norma'
  | 'val'
  | 'testcases'
  | 'marq'
  | 'compli'

// Derived from the workspace registry (single source of truth). The 7
// canvas-bearing ToolIds map 1:1 to registry entries; canvasMetaFor returns
// identical name/href/workspaceHref (asserted in registry.test.ts).
const TOOL_IDS: readonly ToolId[] = ['concierge', 'study-design', 'norma', 'val', 'testcases', 'marq', 'compli']
export const CANVAS_META = Object.fromEntries(
  TOOL_IDS.map((id) => [id, canvasMetaFor(id)]),
) as Record<ToolId, { name: string; href: string; workspaceHref: string }>
